import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Header from "@/components/Header";
import {
  selectNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAll,
  type Notification,
} from "@/redux/slices/notificationsSlice";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BellOff, CheckCheck, Trash2, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSafeArea } from "@/hooks/use-native";

function formatTime(ts: number) {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function NotificationItem({
  notification,
  onMarkRead,
  onDelete,
  onView,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
  onView: (n: Notification) => void;
}) {
  const caseId = notification.data?.case_id ?? notification.data?.entity_id;
  const hasLink = caseId != null;

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 border-b border-border bg-card",
        !notification.read && "bg-muted/30"
      )}
    >
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "font-medium text-sm",
            !notification.read && "text-foreground"
          )}
        >
          {notification.title}
        </p>
        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
          {notification.body}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {formatTime(notification.timestamp)}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!notification.read && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onMarkRead(notification.id)}
            aria-label="Mark as read"
          >
            <CheckCheck className="h-4 w-4" />
          </Button>
        )}
        {hasLink && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onView(notification)}
            aria-label="View case"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          onClick={() => onDelete(notification.id)}
          aria-label="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notifications = useSelector(selectNotifications);
  const safeArea = useSafeArea();

  const handleView = (n: Notification) => {
    dispatch(markAsRead(n.id));
    const caseId = n.data?.case_id ?? n.data?.entity_id;
    if (caseId != null) navigate(`/case/${caseId}`);
  };

  return (
    <div className="min-h-screen bg-background" style={{ paddingTop: safeArea.top }}>
      <Header title="Notifications" showBackButton />
      <div className="pt-14 px-4 pb-4">
        {notifications.length > 0 ? (
          <>
            <div className="flex justify-end gap-2 mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => dispatch(markAllAsRead())}
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => dispatch(clearAll())}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear all
              </Button>
            </div>
            <ScrollArea className="h-[calc(100vh-12rem)] rounded-lg border border-border">
              {notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onMarkRead={(id) => dispatch(markAsRead(id))}
                  onDelete={(id) => dispatch(deleteNotification(id))}
                  onView={handleView}
                />
              ))}
            </ScrollArea>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <BellOff className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground font-medium">No notifications</p>
            <p className="text-sm text-muted-foreground mt-1">
              Case updates and other alerts will appear here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
